/* The sheet scopes this status to no offering, so the section carries no offering strip: a
   programme is the partner's, not the plan's, and the same membership is at risk whichever
   offering the reader stands on. */
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import {
  type IProgramType,
  parseSectionSettings,
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'
import { getCompletenessItems, p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { scrollToElement } from 'partner360/helpers/scrollToElement'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import {
  selectCockpitFirstPartnerOfferingsCatalog,
  selectCockpitPartnerCompleteness,
  selectCockpitPartnerData,
} from '../../../../model/selectors'
import { CockpitSectionHeader } from '../../../components/cockpitSectionHeader'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { CockpitTable, type ICockpitTableColumn } from '../../../components/cockpitTable'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { AttentionStatusActions } from '../attentionStatusActions'

import styles from './AttentionStatusProgramRisk.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.programRisk

/* Keys this section needs and `configs/translations.ts` does not carry yet. Declared here in the
   feature's own namespace so the section reads the same once they are moved into the registry. */

/** What a cell says when the payload states nothing for it, as the neighbouring sections say it. */
const EMPTY_CELL = '--'

interface IProgramRow {
  id: string
  /** What the programme is called. */
  labelKey: string
  /** The membership in the payload's own words, where it carries them. */
  membershipText?: string
  /** The membership as a word of ours, for the programmes the payload states as a bare flag. */
  membershipKey?: string
  /** Offerings whose section settings mark this programme's initiative as required. */
  requiredFor: IProgramType[]
}

/**
 * The offerings a programme's initiative is required for, as the Key initiatives widget records
 * it: one `<programme><program type>Required` setting per offering, the field name built in
 * `legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/hooks/useKeyInitiatives.ts`.
 *
 * These toggles are the only statement of a programme requirement the cockpit payload carries.
 * They say that an initiative is owed, never which initiative — the programme's own requirement
 * list is not in the payload at all.
 */
const getRequiredOfferings = (
  settings: Record<string, unknown>,
  programTypes: IProgramType[],
  program: 'AmplifyImpact' | 'GrowthPlay'
) =>
  programTypes.filter(
    ({ programType }) =>
      Number(settings[`${program}${programType.replace(/\s/g, '')}Required`]) === 1
  )

/**
 * The detail of the `Specific program risk (Amplify Impact, GrowthPlay, etc)` status: the
 * programmes the partner belongs to, and the offerings whose plan is required to carry each
 * programme's initiative.
 *
 * The sheet asks this card for the list of initiatives the partner is missing, with a create call
 * on each row. The cockpit payload does not carry that list: it states which programmes the
 * partner is in (`AmplifyTrack` with its earned tier, `AmplifyImpactIsEligible`, `GrowthPlay`) and
 * which offerings owe a programme initiative (the `Required` section settings), but never which
 * initiatives a programme asks for, nor which of the partner's own initiatives answers one. So the
 * table states what is owed and the note under it says plainly what is still missing from the
 * payload, rather than naming requirements nobody sent.
 *
 * The condition itself is the server's: the payload sends the status only once it is raised, and
 * this section never re-decides whether the membership is at risk.
 */
export const AttentionStatusProgramRisk = () => {
  const { isRemote, openSection } = useCockpitSectionNavigation()
  const partnerData = useSelector(selectCockpitPartnerData)
  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const completenessProfiles = useSelector(selectCockpitPartnerCompleteness)
  const { isDistributor } = useSelector(selectPartnerAccess)

  const partner = partnerData[0]

  /* An offering the partner has switched off owes nothing: its plan is out of the count, and so is
     the programme initiative its settings once asked for. */
  const programTypes = useMemo(
    () => getAvailableProgramTypes(offeringsCatalog).filter(({ enabled }) => enabled),
    [offeringsCatalog]
  )

  /* The settings are the JSON string of the partner's own 621 record, read with the parser the
     partner page reads them with — a malformed or absent string is no settings there too, so the
     card and the page never disagree on what the PBM asked for. */
  const sectionSettings = useMemo<Record<string, unknown>>(
    () => parseSectionSettings(partner?.SectionSettings ?? null),
    [partner?.SectionSettings]
  )

  const rows = useMemo<IProgramRow[]>(() => {
    if (!partner) {
      return []
    }

    const amplifyImpactRequiredFor = getRequiredOfferings(
      sectionSettings,
      programTypes,
      'AmplifyImpact'
    )
    const growthPlayRequiredFor = getRequiredOfferings(sectionSettings, programTypes, 'GrowthPlay')
    const programs: IProgramRow[] = []

    /* The track is the Amplify membership itself, and the cockpit's mapper has already appended
       the Power Elite tier the partner earned — so the row states both in the payload's words
       rather than translating a tier the payload may name differently next year. */
    if (partner.AmplifyTrackWithMembership) {
      programs.push({
        id: 'amplifyTrack',
        labelKey: KEYS.amplifyTrack,
        membershipText: partner.AmplifyTrackWithMembership,
        requiredFor: [],
      })
    }

    /* A programme shows on its own flag, and also on a requirement alone: a partner whose PBM has
       marked the initiative owed is in the programme whatever the eligibility flag says today. */
    if (partner.AmplifyImpactIsEligible === 1 || amplifyImpactRequiredFor.length > 0) {
      programs.push({
        id: 'amplifyImpact',
        labelKey: KEYS.amplifyImpact,
        membershipKey: partner.AmplifyImpactIsEligible === 1 ? KEYS.eligible : undefined,
        requiredFor: amplifyImpactRequiredFor,
      })
    }

    if (partner.GrowthPlay === 1 || growthPlayRequiredFor.length > 0) {
      programs.push({
        id: 'growthPlay',
        labelKey: KEYS.growthPlay,
        membershipKey: partner.GrowthPlay === 1 ? KEYS.member : undefined,
        requiredFor: growthPlayRequiredFor,
      })
    }

    return programs
  }, [partner, programTypes, sectionSettings])

  /* `Go to section` opens the initiatives plan on the partner page. The completeness catalog is
     where the cockpit already keeps how each section is opened, and every profile carries the same
     row for it, so the first profile that states it decides where the link goes. */
  const initiativesPlanItem = useMemo(
    () =>
      completenessProfiles
        .flatMap((profile) => getCompletenessItems(profile, isDistributor))
        .find(({ id }) => id === 'initiativesPlan'),
    [completenessProfiles, isDistributor]
  )

  const exitDetail = useAttentionDetailExit()
  const programType = useSelector(selectProgramType)

  /* The sheet's first call on this status is a new initiative — the programme is at risk because
     one is missing — so the card opens the initiative form over itself rather than sending the
     reader to the plan to find the same form there. */
  const [isCreating, setIsCreating] = useState(false)
  const handleCloseEditor = useCallback(() => setIsCreating(false), [])

  const handleOpenInitiatives = useCallback(() => {
    scrollToElement(initiativesPlanItem?.scrollElementId ?? p360TabsIds)

    if (initiativesPlanItem?.urlParams) {
      openSection(initiativesPlanItem.urlParams)
    }

    exitDetail()
  }, [exitDetail, initiativesPlanItem, openSection])

  /* The form is the partner page's legacy dialog and reads that page's own partner, so away from
     the page the initiative is created on the partner's initiatives plan instead. */
  const handleCreateInitiative = useCallback(() => {
    if (isRemote) {
      handleOpenInitiatives()
      return
    }

    setIsCreating(true)
  }, [handleOpenInitiatives, isRemote])

  const columns: ICockpitTableColumn<IProgramRow>[] = [
    {
      key: 'program',
      header: <Translation span text={KEYS.columnProgram} />,
      renderCell: ({ labelKey }) => <Translation span text={labelKey} />,
    },
    {
      key: 'membership',
      header: <Translation span text={KEYS.columnMembership} />,
      renderCell: ({ membershipText, membershipKey }) =>
        membershipText ?? (membershipKey ? <Translation span text={membershipKey} /> : EMPTY_CELL),
    },
    {
      key: 'required',
      header: <Translation span text={KEYS.columnRequired} />,
      /* The offerings are listed in one cell, so the comma between them is drawn beside each
         translated name rather than joined into a single string. */
      renderCell: ({ requiredFor }) =>
        requiredFor.length > 0
          ? requiredFor.map(({ programType }, index) => (
              <Fragment key={programType}>
                {index > 0 && ', '}
                <Translation span text={getOfferingTitleKey(programType)} />
              </Fragment>
            ))
          : EMPTY_CELL,
    },
  ]

  return (
    <div className={styles.root}>
      {/* The lead sentence is the placeholder's — every opened status says what it shows through
        `jbp-attention-statuses-intro-program-risk` before its section starts, so the card opens on
        its own content rather than on a second paragraph saying the same thing. */}
      {rows.length === 0 ? (
        /* The cockpit knows the partner's programmes from its own payload, so a partner that
           states none gets a sentence rather than an empty table under a header. */
        <p className={styles.empty}>
          <Translation span text={KEYS.noPrograms} />
        </p>
      ) : (
        <>
          <CockpitSectionHeader>
            <Translation span text={KEYS.title} />
          </CockpitSectionHeader>

          <CockpitTable columns={columns} items={rows} getItemKey={({ id }) => id} />

          {/* Where the sheet's per-initiative create calls will stand. Until the payload names the
            initiatives a programme requires, the card says so instead of listing guesses. */}
          <p className={styles.notice}>
            <Translation span text={KEYS.requirementsUnknown} />
          </p>
        </>
      )}

      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.programRisk}
        className={styles.actions}
        handlers={{
          createInitiative: handleCreateInitiative,
          goToSection: handleOpenInitiatives,
        }}
      />

      {isCreating && (
        /* The form opens empty: a programme requirement names no investment source of its own, so
           nothing is filled in for the reader to correct. */
        <KeyInitiativeEditor
          isDialogOpened
          isDataInsightPresent={programType !== ProgramTypes.Retail}
          onEditCancel={handleCloseEditor}
          onUpdateFinished={handleCloseEditor}
        />
      )}
    </div>
  )
}

AttentionStatusProgramRisk.displayName = 'AttentionStatusProgramRisk'
